import React from 'react';

import { mdiArrowLeft } from '@lumapps/lumx/icons';
import { Alignment, Emphasis, FlexBox, IconButton, Orientation, type GapSize } from '@lumapps/lumx/react';
import { MOVE_DOCUMENT_FOLDER_STATUS } from '@lumapps/medias-document/constants';
import type { MoveDocumentNavigationState } from '@lumapps/medias-document/types';
import { GLOBAL, useTranslate } from '@lumapps/translations';

export interface MediaLibraryBaseBreadcrumbProps {
    /** The content of the breadcrumb. */
    children?: React.ReactNode;
    /** An optional class name to apply to the root element. */
    className?: string;
    /** The state object from the useMoveDocumentNavigation hook. */
    folderState: MoveDocumentNavigationState;
    /** Callback function to navigate to the parent folder. */
    navigateToParent: () => void;
    gap?: GapSize;
}

/**
 * A generic breadcrumb component that provides a back button and a flexible content area.
 *
 * @param {MediaLibraryBaseBreadcrumbProps}
 * @returns MediaLibraryBaseBreadcrumb
 */
export const MediaLibraryBaseBreadcrumb: React.FC<MediaLibraryBaseBreadcrumbProps> = ({
    children,
    className,
    folderState,
    navigateToParent,
    gap,
}: MediaLibraryBaseBreadcrumbProps) => {
    const { translateKey } = useTranslate();

    const isLoading = folderState.status === MOVE_DOCUMENT_FOLDER_STATUS.LOADING;
    const isBackButtonVisible = folderState.status !== MOVE_DOCUMENT_FOLDER_STATUS.ROOT;

    return (
        <FlexBox className={className} gap={gap} hAlign={Alignment.center} orientation={Orientation.horizontal}>
            {/* The back button is shown for any state that isn't the root. */}
            {isBackButtonVisible && (
                <IconButton
                    emphasis={Emphasis.low}
                    icon={mdiArrowLeft}
                    isDisabled={isLoading}
                    label={translateKey(GLOBAL.PREVIOUS)}
                    onClick={navigateToParent}
                />
            )}

            {children}
        </FlexBox>
    );
};
